import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Button,
  Modal,
  Typography,
  Alert,
  InputAdornment,
  TextField,
} from "@mui/material";
import { CirclePlus, Search } from "lucide-react";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteConfirmationModal from "../../components/shared/modals/DeleteConfirmationModal";
import {
  getAdjustments,
  createAdjustment,
  updateAdjustment,
  deleteAdjustment,
} from "../../api/Adjustment/api";
import usePermissions from "./hooks/usePermissions";
import DataTable from "../../components/DataTable";
import AdjustmentForm from "../../components/shared/forms/AdjustmentForm";
import { AuthContext } from "../../contexts/AuthContext";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 1,
  p: 0,
};

const AdjustmentManagement = () => {
  const {
    loading: loadingPermissions,
    error: permissionsError,
    can,
  } = usePermissions();
  const { user } = useContext(AuthContext);
  const [adjustment, setAdjustment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [selectedAdjustment, setSelectedAdjustment] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [adjustmentToDelete, setAdjustmentToDelete] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    if (!loadingPermissions && can.viewAdjustment()) {
      fetchAdjustment();
    }
  }, [loadingPermissions]);

  const fetchAdjustment = async () => {
    try {
      setLoading(true);
      const response = await getAdjustments();

      if (response?.data) {
        const transformedData = response.data.map((adjustment) => {
          return {
            id: adjustment.id,
            duka: adjustment.duka,
            product: adjustment.product,
            adjustmentType: adjustment.adjustment_type,
            quantity: adjustment.quantity,
            reason: adjustment.reason,
            adjustedDate: adjustment.adjusted_date.split("T")[0],
            // Keep original data for form population
            original: adjustment,
          };
        });
        console.log("Adjustment loaded:", transformedData);
        setAdjustment(transformedData);
      }

      setError(null);
    } catch (err) {
      console.error("Error fetching Adjustment:", err);
      setError("Failed to load Adjustment. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const transformAdjustmentData = (data) => {
    return {
      duka: data.duka,
      product: data.product,
      adjustment_type: data.adjustment_type,
      quantity: parseInt(data.quantity),
      reason: data.reason,
      adjusted_date: data.adjusted_date,
    };
  };

  const handleCreateAdjustment = async (data) => {
    try {
      const transformedData = transformAdjustmentData(data);

      await createAdjustment(transformedData);
      setOpenModal(false);
      fetchAdjustment();
    } catch (error) {
      console.error("Error creating adjustment:", error);
      setError(
        error.response?.data?.message ||
          "Failed to create adjustment. Please try again."
      );
    }
  };

  const handleUpdateAdjustment = async (id, data) => {
    try {
      const transformedData = transformAdjustmentData(data);
      await updateAdjustment(id, transformedData);
      setOpenModal(false);
      setSelectedAdjustment(null);
      fetchAdjustment();
    } catch (error) {
      console.error("Error updating adjustment:", error);
      setError(
        error.response?.data?.message ||
          "Failed to update adjustment. Please try again."
      );
    }
  };

  const handleDeleteAdjustment = async () => {
    if (!adjustmentToDelete) return;

    try {
      await deleteAdjustment(adjustmentToDelete.id);
      setDeleteModalOpen(false);
      setAdjustmentToDelete(null);
      fetchAdjustment();
    } catch (error) {
      console.error("Error deleting adjustment:", error);
      setError("Failed to delete adjustment. Please try again.");
    }
  };

  const handleSubmit = (data) => {
    if (selectedAdjustment) {
      handleUpdateAdjustment(selectedAdjustment.id, data);
    } else {
      handleCreateAdjustment(data);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredAdjustment = adjustment.filter((adjustment) =>
    Object.values(adjustment).some(
      (value) =>
        value &&
        value.toString().toLowerCase().includes(searchQuery.toLowerCase())
    )
  );

  const columns = [
    { id: "duka", label: "Duka" },
    { id: "product", label: "Product" },
    { id: "adjustmentType", label: "Adjustment Type" },
    { id: "quantity", label: "Quantity" },
    { id: "reason", label: "Reason" },
    { id: "adjustedDate", label: "Adjusted Date" },
  ];

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{ color: "#124F3E", fontWeight: "bold", mb: 3 }}
      >
        Manage Adjustment
      </Typography>

      {(error || permissionsError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || permissionsError}
        </Alert>
      )}

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <TextField
          placeholder="Search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: "300px" }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={20} />
              </InputAdornment>
            ),
            sx: { height: "40px" },
          }}
        />

        {can.createAdjustment() && (
          <Button
            variant="contained"
            startIcon={<CirclePlus size={18} />}
            onClick={() => {
              setSelectedAdjustment(null);
              setOpenModal(true);
            }}
            sx={{
              fontWeight: "bold",
              bgcolor: "#124F3E",
              "&:hover": {
                backgroundColor: "#1a614d !important",
              },
            }}
          >
            Add Adjustment Level
          </Button>
        )}
      </Box>

      <DataTable
        loading={loading || loadingPermissions}
        columns={columns}
        rows={filteredAdjustment}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        onEdit={
          can.editAdjustment()
            ? (rowData) => {
                setSelectedAdjustment(rowData);
                setOpenModal(true);
              }
            : undefined
        }
        onDelete={
          can.deleteAdjustment()
            ? (rowData) => {
                setAdjustmentToDelete(rowData);
                setDeleteModalOpen(true);
              }
            : undefined
        }
        editIcon={<EditIcon />}
        deleteIcon={<DeleteIcon />}
        editTooltip="Edit"
        deleteTooltip="Delete"
        editDisabled={!can.editAdjustment()}
        deleteDisabled={!can.deleteAdjustment()}
      />

      {(can.createAdjustment() || can.editAdjustment()) && (
        <Modal
          open={openModal}
          onClose={() => {
            setSelectedAdjustment(null);
            setOpenModal(false);
          }}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={modalStyle}>
            <AdjustmentForm
              initialData={selectedAdjustment?.original || null}
              onSubmit={handleSubmit}
              onCancel={() => {
                setSelectedAdjustment(null);
                setOpenModal(false);
              }}
            />
          </Box>
        </Modal>
      )}

      {can.deleteAdjustment() && (
        <DeleteConfirmationModal
          open={deleteModalOpen}
          onClose={() => {
            setDeleteModalOpen(false);
            setAdjustmentToDelete(null);
          }}
          onConfirm={handleDeleteAdjustment}
          title="Delete adjustment"
          message="Are you sure you want to delete this adjustment?"
          itemName={adjustmentToDelete?.name}
        />
      )}
    </Box>
  );
};

export default AdjustmentManagement;
